/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import {
  Button,
  Checkbox,
  CheckedState,
  FormLabel,
  sizes,
  Spacer,
  Tooltip,
  TooltipBody,
  useTooltip,
  TypeRampV2,
} from '@smartsheet/lodestar-core';
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import { UpsertScheduleType } from '../../../../../../../services/api/SchedulesService';
import {
  SchedulesState,
  selectSchedules,
  selectRunOptions,
  selectUnitOptions,
  setIsUpsertScheduleVisible,
  setSelectedScheduleIndex,
  upsertScheduleData,
} from '../../../../../../../services/ducks/schedulesDuck';
import { workflowUUIDSelector } from '../../../../../../../services/ducks/workflowDuck/workflowDuck';
import { findSavedOptionFromValue } from '../../../../../../../utils/selectMenu';
import { Frequency } from './Frequency';
import { StartDate } from './StartDate';
import { StartTime } from './StartTime';
import {
  ALL_START_TIME_OPTIONS,
  getHoursMinutesFromOptionValue,
  ScheduleStateDatePickerType,
  ScheduleStateSelectType,
  ScheduleStateStringType,
} from './utils';

const buttonsContainer = css`
  margin-top: ${sizes.xLarge}px;
`;

const titleContainer = css`
  margin-bottom: ${sizes.small}px;
`;

export const UpsertSchedule = ({
  scheduleIndex,
}: {
  scheduleIndex: SchedulesState['selectedScheduleIndex'];
}) => {
  const isScheduleIndexSet = scheduleIndex !== undefined;
  const workflowUUID = useSelector(workflowUUIDSelector);
  const savedSchedules = useSelector(selectSchedules);
  const RUN_OPTIONS = useSelector(selectRunOptions);
  const UNIT_OPTIONS = useSelector(selectUnitOptions);
  const [startDate, setStartDate] = useState<ScheduleStateDatePickerType>({
    value: null,
    valid: false,
  });
  const [startTime, setStartTime] = useState<ScheduleStateSelectType>({
    value: null,
    valid: false,
  });
  const [run, setRun] = useState<ScheduleStateSelectType>({
    value: null,
    valid: false,
  });
  const [unit, setUnit] = useState<ScheduleStateSelectType>({
    value: UNIT_OPTIONS[2],
    valid: true,
  });
  const [duration, setDuration] = useState<ScheduleStateStringType>({
    value: '1',
    valid: true,
  });
  const [runOnLastDayOfMonth, setrunOnLastDayOfMonth] =
    useState<CheckedState>('unchecked');
  const { t: translate } = useTranslation();
  const { tooltipProps, targetProps } = useTooltip();
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      isScheduleIndexSet &&
      Array.isArray(savedSchedules) &&
      savedSchedules[scheduleIndex]
    ) {
      const savedSchedule = savedSchedules[scheduleIndex];
      const startAt = new Date(savedSchedule.startAt);
      setStartDate({
        value: {
          year: startAt.getUTCFullYear(),
          month: startAt.getUTCMonth(),
          day: startAt.getUTCDate(),
        },
        valid: true,
      });

      setStartTime({
        value: findSavedOptionFromValue(
          ALL_START_TIME_OPTIONS,
          savedSchedule.startAt.slice(11, 16) || ''
        ),
        valid: true,
      });

      const initialRunValue = findSavedOptionFromValue(
        RUN_OPTIONS,
        savedSchedule.frequency
      );
      setRun({ value: initialRunValue || RUN_OPTIONS[6], valid: true });

      if (!initialRunValue) {
        const splitFreq = savedSchedule.frequency.split(' ');
        setUnit({
          value: findSavedOptionFromValue(UNIT_OPTIONS, splitFreq[2]),
          valid: true,
        });
        setDuration({ value: splitFreq[1], valid: true });
      }
    }
  }, [
    RUN_OPTIONS,
    UNIT_OPTIONS,
    savedSchedules,
    scheduleIndex,
    isScheduleIndexSet,
  ]);

  const hideUpsert = () => {
    batch(() => {
      dispatch(setSelectedScheduleIndex(undefined));
      dispatch(setIsUpsertScheduleVisible(false));
    });
  };

  const saveScheduleHandler = () => {
    if (isSaveEnabled) {
      let startDateTimeISO = undefined;
      if (startDate.value && startTime.value) {
        const [hours, minutes] = getHoursMinutesFromOptionValue(
          startTime.value.value
        );
        startDateTimeISO = new Date(
          Date.UTC(
            startDate.value.year,
            startDate.value.month,
            startDate.value.day,
            hours,
            minutes
          )
        ).toISOString();
      }

      let frequency = run.value?.value;
      if (frequency === 'Custom') {
        frequency = `@every ${duration.value} ${unit.value?.value}`;
      }
      const scheduleData: UpsertScheduleType = {
        workflowID: workflowUUID,
        startAt: startDateTimeISO || '',
        frequency: frequency || '',
        forceLastDay: runOnLastDayOfMonth === 'checked' ? true : false,
      };
      if (scheduleIndex) {
        scheduleData.id = savedSchedules[scheduleIndex].id;
      }
      dispatch(upsertScheduleData(scheduleData));
      hideUpsert();
    }
  };

  const getNextCheckboxValue = (currentValue: CheckedState) => {
    return currentValue === 'checked' ? 'unchecked' : 'checked';
  };
  const shouldRunOnLastDayBeVisible =
    unit.value?.value === 'months' || run.value?.value.includes('months');

  const [disabledSaveTooltipMessage, setDisabledSaveTooltipMessage] =
    useState('');

  const isSaveEnabled = useMemo(() => {
    const fieldLabels = [
      translate('designer:triggerPanel:schedules:upsert:startDate'),
      translate('designer:triggerPanel:schedules:upsert:startTime'),
      translate('designer:triggerPanel:schedules:upsert:run'),
      translate('designer:triggerPanel:schedules:upsert:unit'),
      translate('designer:triggerPanel:schedules:every'),
    ];
    const fieldStatuses = [
      startDate.valid,
      startTime.valid,
      run.valid,
      unit.valid,
      duration.valid,
    ];
    const invalidFieldMessages: string[] = [];
    fieldStatuses.forEach((status, index) => {
      if (!status) {
        invalidFieldMessages.push(fieldLabels[index]);
      }
    });
    if (invalidFieldMessages.length > 1) {
      setDisabledSaveTooltipMessage(
        `${invalidFieldMessages.join(', ')} ${translate(
          'designer:triggerPanel:schedules:upsert:areRequiredFields'
        )}`
      );
    } else if (invalidFieldMessages.length > 0) {
      setDisabledSaveTooltipMessage(
        `${invalidFieldMessages[0]} ${translate(
          'designer:triggerPanel:schedules:upsert:isArequiredFeild'
        )}`
      );
    }
    return !(invalidFieldMessages.length > 0);
  }, [
    startDate.valid,
    startTime.valid,
    run.valid,
    unit.valid,
    duration.valid,
    translate,
  ]);

  return (
    <div>
      <div css={titleContainer}>
        <TypeRampV2 system="lodestar" variant="titlesEmphasis" renderAs="h5">
          {`${translate('designer:triggerPanel:schedules:schedule')} ${
            isScheduleIndexSet ? scheduleIndex + 1 : savedSchedules.length + 1
          }`}
        </TypeRampV2>
      </div>
      <form onSubmit={saveScheduleHandler}>
        <Spacer orientation="column" space="large">
          <StartDate
            startDate={startDate}
            setStartDate={setStartDate}
            isRequired={true}
          />
          <StartTime
            startTime={startTime}
            setStartTime={setStartTime}
            startDate={startDate}
            isRequired={true}
          />
          <Frequency
            run={run}
            duration={duration}
            unit={unit}
            setRun={setRun}
            setDuration={setDuration}
            setUnit={setUnit}
            isRequired={true}
          />
          {shouldRunOnLastDayBeVisible ? (
            <FormLabel
              validationMessage={translate(
                'designer:triggerPanel:schedules:upsert:runOnLastDayOfMonthInfo'
              )}
              validationState={'info'}
              label=""
            >
              {labelProps => (
                <Checkbox
                  {...labelProps}
                  label={translate(
                    'designer:triggerPanel:schedules:upsert:runOnLastDayOfMonth'
                  )}
                  checkedState={runOnLastDayOfMonth}
                  onClick={() => {
                    setrunOnLastDayOfMonth(
                      getNextCheckboxValue(runOnLastDayOfMonth)
                    );
                  }}
                />
              )}
            </FormLabel>
          ) : null}
        </Spacer>
        <Spacer css={buttonsContainer} orientation="row" space="small">
          <Button appearance="secondary" onClick={hideUpsert}>
            {translate('common:cancel')}
          </Button>
          <Button
            onClick={saveScheduleHandler}
            {...targetProps}
            isDisabled={!isSaveEnabled}
          >
            {translate('common:save')}
          </Button>
          {!isSaveEnabled ? (
            <Tooltip {...tooltipProps}>
              <TooltipBody>{disabledSaveTooltipMessage}</TooltipBody>
            </Tooltip>
          ) : null}
        </Spacer>
      </form>
    </div>
  );
};
